import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './ListingDetails.css';

function ListingDetails({ user }) {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    fetchListing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchListing = async () => {
    try {
      const response = await axios.get(`/api/listings/${id}`);
      setListing(response.data);
    } catch (error) {
      console.error('Error fetching listing:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevImage = () => {
    setActiveImageIndex(prev => 
      prev === 0 ? listing.images.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setActiveImageIndex(prev => 
      prev === listing.images.length - 1 ? 0 : prev + 1
    );
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!listing) {
    return <div className="error">Listing not found</div>;
  }

  const hasMultipleImages = listing.images && listing.images.length > 1;

  return (
    <div className="listing-details">
      <div className="listing-details-container">
        {/* Image Gallery */}
        <div className="listing-gallery">
          <div className="listing-main-image">
            {listing.images && listing.images.length > 0 ? (
              <>
                <img 
                  src={listing.images[activeImageIndex].image_url} 
                  alt={`${listing.title} - Photo ${activeImageIndex + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                {hasMultipleImages && (
                  <>
                    <button className="gallery-arrow gallery-arrow-left" onClick={handlePrevImage}>
                      ‹
                    </button>
                    <button className="gallery-arrow gallery-arrow-right" onClick={handleNextImage}>
                      ›
                    </button>
                  </>
                )}
              </>
            ) : (
              'No Images Available'
            )}
          </div>
          {hasMultipleImages && (
            <div className="gallery-thumbnails">
              {listing.images.map((image, index) => (
                <div 
                  key={index}
                  className={`gallery-thumbnail ${index === activeImageIndex ? 'active' : ''}`}
                  onClick={() => setActiveImageIndex(index)}
                >
                  <img src={image.image_url} alt={`Thumbnail ${index + 1}`} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Header */}
        <div className="listing-header">
          <div className="listing-header-top">
            <div className="listing-price">${listing.price.toLocaleString()}</div>
            <div className="listing-badges">
              <span className={`listing-status listing-status-${listing.status}`}>
                {listing.status}
              </span>
              {listing.listing_type === 'seller_finance' && (
                <span className="listing-financing-badge">Seller Financing</span>
              )}
            </div>
          </div>
          <div className="listing-address">{listing.address}</div>
          <div className="listing-city">{listing.city}, {listing.state} {listing.zip_code}</div>
          
          <div className="listing-stats">
            {listing.bedrooms && (
              <div className="listing-stat">
                <div className="listing-stat-value">{listing.bedrooms}</div>
                <div className="listing-stat-label">Bedrooms</div>
              </div>
            )}
            {listing.bathrooms && (
              <div className="listing-stat">
                <div className="listing-stat-value">{listing.bathrooms}</div>
                <div className="listing-stat-label">Bathrooms</div>
              </div>
            )}
            {listing.square_feet && (
              <div className="listing-stat">
                <div className="listing-stat-value">{listing.square_feet.toLocaleString()}</div>
                <div className="listing-stat-label">Sq Ft</div>
              </div>
            )}
            {listing.lot_size && (
              <div className="listing-stat">
                <div className="listing-stat-value">{listing.lot_size}</div>
                <div className="listing-stat-label">Acres</div>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="listing-section">
          <h2>Description</h2>
          <p>{listing.description || 'No description provided.'}</p>
        </div>

        {/* Property Details */}
        <div className="listing-section">
          <h2>Property Details</h2>
          <div className="listing-facts">
            <div className="listing-fact">
              <span className="listing-fact-label">Property Type</span>
              <span className="listing-fact-value">{listing.property_type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</span>
            </div>
            {listing.year_built && (
              <div className="listing-fact">
                <span className="listing-fact-label">Year Built</span>
                <span className="listing-fact-value">{listing.year_built}</span>
              </div>
            )}
            <div className="listing-fact">
              <span className="listing-fact-label">Status</span>
              <span className="listing-fact-value listing-fact-active">{listing.status}</span>
            </div>
            <div className="listing-fact">
              <span className="listing-fact-label">Listed</span>
              <span className="listing-fact-value">{new Date(listing.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        {/* Contact Seller */}
        <div className="listing-section">
          <h2>Contact Seller</h2>
          <div className="listing-seller-info">
            <div className="listing-seller-name">{listing.seller_first_name} {listing.seller_last_name}</div>
            <div className="listing-seller-role">Property Owner</div>
            <div className="listing-contact-info">
              {listing.seller_phone && (
                <div className="listing-contact-row">
                  <span className="listing-contact-label">Phone</span>
                  <a href={`tel:${listing.seller_phone}`}>{listing.seller_phone}</a>
                </div>
              )}
              <div className="listing-contact-row">
                <span className="listing-contact-label">Email</span>
                <a href={`mailto:${listing.seller_email}`}>{listing.seller_email}</a>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default ListingDetails;
